<template>
  <div
    class="tei-layout-container"
  >
    <div class="tei-layout-container__content">
      <slot />
    </div>
    <div class="tei-layout-container__footer">
      <TEIScoringBar
        v-if="!showResponseBar"
        :aria-hidden="!showScoringBar"
        :asset-id="assetId"
        v-bind="scoringBar"
      />
      <transition
        name="tei-layout-container__response-bar-transition"
        enter-from-class="tei-layout-container__response-bar-transition--enter-from"
        enter-active-class="tei-layout-container__response-bar-transition--enter-active"
        leave-active-class="tei-layout-container__response-bar-transition--leave-active"
        leave-to-class="tei-layout-container__response-bar-transition--leave-to"
      >
        <TEIResponseBar
          class="tei-layout-container__response-bar"
          :centered="!actionButtonsVisible"
          :can-try-again="showTryAgainButton"
          v-if="showResponseBar"
          v-bind="responseBar"
          :alert-enabled="responseBarAlertEnabled"
        />
      </transition>
    </div>
    <div
      v-if="actionButtonsVisible"
      class="tei-layout-container__action-buttons"
    >
      <TEIButton
        v-if="showResetButton"
        :aria-label="$t('Clear answers')"
        :disabled="!resetButtonEnabled"
        @click="$emit('reset-button-clicked', $event)"
      >
        <RefreshIcon class="tei-layout-container__button-icon" />
      </TEIButton>
      <TEIButton
        v-if="showCheckButton"
        :disabled="!checkButtonEnabled"
        :text="checkButtonText || $t('Check')"
        @click="$emit('check-button-clicked', $event)"
      >
        <CaretRightIcon class="tei-layout-container__button-icon" />
      </TEIButton>
      <TEIButton
        v-else-if="showTryAgainButton"
        :text="$t('Try Again')"
        color="secondary"
        @click="$emit('try-again-button-clicked', $event)"
      >
        <CaretRightIcon class="tei-layout-container__button-icon" />
      </TEIButton>
      <TEIButton
        v-else-if="showCheckingButton"
        :text="checkingButtonText || $t('Checking...')"
        state="active"
        disabled
      />
    </div>
  </div>
</template>

<script>
import TEIScoringBar from '@/components/teis/TEIScoringBar.vue';
import TEIResponseBar from '@/components/teis/TEIResponseBar.vue';
import TEIButton from '@/components/teis/TEIButton.vue';
import CaretRightIcon from '@/components/ui-elements/texas-icons/CaretRightIcon.vue';
import RefreshIcon from '@/components/ui-elements/texas-icons/RefreshIcon.vue';

export default {
  name: 'TEILayoutContainer',
  components: {
    CaretRightIcon,
    TEIScoringBar,
    TEIResponseBar,
    TEIButton,
    RefreshIcon,
  },
  props: {
    resetButtonEnabled: {
      type: Boolean,
      default: false,
    },
    checkButtonEnabled: {
      type: Boolean,
      default: false,
    },
    showResetButton: {
      type: Boolean,
      default: false,
    },
    showCheckButton: {
      type: Boolean,
      default: true,
    },
    showTryAgainButton: {
      type: Boolean,
      default: false,
    },
    showCheckingButton: {
      type: Boolean,
      default: false,
    },
    showScoringBar: {
      type: Boolean,
      default: true,
    },
    showResponseBar: {
      type: Boolean,
      default: false,
    },
    scoringBar: {
      type: Object,
      default: null,
    },
    responseBar: {
      type: Object,
      default: null,
    },
    isDisplayed: {
      type: Boolean,
      default: true,
    },
    checkButtonText: {
      type: String,
      default: null,
    },
    checkingButtonText: {
      type: String,
      default: null,
    },
    assetId: {
      type: String,
      default: null,
    },
  },
  emits: [
    'check-button-clicked',
    'reset-button-clicked',
    'try-again-button-clicked',
  ],
  data() {
    return {
      responseBarAlertEnabled: false,
    };
  },
  watch: {
    showResponseBar() {
      if (this.showResponseBar) {
        this.responseBarAlertEnabled = true;
      }
    },
    isDisplayed() {
      if (!this.isDisplayed) {
        this.responseBarAlertEnabled = false;
      }
    },
  },
  computed: {
    actionButtonsVisible() {
      return this.showResetButton || this.showCheckButton
        || this.showCheckingButton || this.showTryAgainButton;
    },
  },
};
</script>

<style lang="stylus">
@import './styles/tei-theme';
@import './styles/tei-fonts';

.tei-layout-container {
  tei-theme();
  tei-mikado-body-small-regular();

  background-color: var(--tei-background-color);
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  width: 100%;

  &__content {
    flex: 1 1 auto;
    overflow: hidden;
  }

  &__footer {
    flex: 0 1 auto;
  }

  &__action-buttons {
    align-items: center;
    bottom: $nebula-space-2x;
    display: flex;
    gap: $nebula-space-1x;
    inset-inline-end: $nebula-space-3x;
    justify-content: center;
    position: absolute;
    z-index: 1;
  }

  &__button-icon {
    display: block;
    height: $nebula-space-3x;
    width: $nebula-space-3x;
  }

  &__response-bar {
    bottom: 0;
    position: absolute;
  }

  &__response-bar-transition {
    &--enter-active,
    &--leave-active {
      transition: $nebula-transition-default;
    }

    &--enter-from,
    &--leave-to {
      transform: translateY(100%);
    }
  }
}
</style>
